import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Btn } from "../components/UI";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ChevronLeft, Settings2, Image as ImageIcon, Mic, Wand2, PackageCheck, BarChart3 } from "lucide-react";
import { StepSetup, StepMedia, StepVoice, StepGenerate, StepExport, StepPerformance } from "./adstudio/steps";

const STEPS = [
  { key: "setup", label: "Setup", icon: Settings2 },
  { key: "media", label: "Media", icon: ImageIcon },
  { key: "voice", label: "Script & Voice", icon: Mic },
  { key: "generate", label: "Generate", icon: Wand2 },
  { key: "export", label: "Export", icon: PackageCheck },
  { key: "performance", label: "Performance", icon: BarChart3 },
];

export default function AdStudioProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/ad-studio/projects/${id}`);
      setProject(r.data);
      return r.data;
    } catch {
      toast.error("Project not found");
      navigate("/ad-studio");
    }
  }, [id, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (patch, silent = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/ad-studio/projects/${id}`, patch);
      setProject(r.data);
      if (!silent) toast.success("Saved");
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [id]);

  if (!project) return <div className="p-8 text-slate-400 text-sm">Loading ad project…</div>;

  const StepComp = [StepSetup, StepMedia, StepVoice, StepGenerate, StepExport, StepPerformance][step];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="ad-studio-project-page">
      <button onClick={() => navigate("/ad-studio")} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 mb-3" data-testid="back-to-studio">
        <ChevronLeft className="w-3.5 h-3.5" /> Ad Studio
      </button>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight" data-testid="project-title">{project.name}</h1>
          <p className="text-xs text-slate-400 mt-1">
            {project.candidate_name || "—"} · {project.party} · <span className="text-cyan-300">{project.disclaimer}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 overflow-x-auto" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => setStep(i)}
              data-testid={`step-tab-${s.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs whitespace-nowrap border transition-colors ${
                i === step
                  ? "bg-gradient-to-r from-cyan-400/20 to-blue-500/10 border-cyan-400/30 text-cyan-300"
                  : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <s.icon className="w-3.5 h-3.5" /> {i + 1}. {s.label}
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-white/10 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <StepComp project={project} refresh={refresh} save={save} saving={saving} />

      <div className="mt-8 flex items-center justify-between">
        <Btn variant="secondary" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev">
          <ArrowLeft className="w-4 h-4" /> Back
        </Btn>
        {step < STEPS.length - 1 && (
          <Btn onClick={() => setStep(step + 1)} data-testid="wizard-next">
            Next: {STEPS[step + 1].label} <ArrowRight className="w-4 h-4" />
          </Btn>
        )}
      </div>
    </div>
  );
}
